/**
 * HBarChart: ranked categories (top customers, per tech), in the order the report sends them, value at the bar tip in
 * a text token. Height = rows x 28 px. A null value draws no bar and reads "no data". Owner: slice web-charts (#1115).
 */
import * as React from 'react'
import { HEIGHT, INK, MARK } from './chart-theme'
import { ChartTooltip, type TooltipRow, tooltipText } from './chart-tooltip'
import { formatValue, xLabel } from './format'
import { hBarPath, linear, useWidth } from './geometry'
import { summarise } from './line-chart'
import { MarkButton } from './mark-button'
import { buildModel, type ChartProps } from './model'

export function HBarChart({ chart, onPointClick, canSee }: ChartProps) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const model = React.useMemo(() => buildModel(chart, canSee), [chart, canSee])
  const [hover, setHover] = React.useState<{ i: number; s: number } | null>(null)
  const { xs, plotted } = model
  const row = HEIGHT.hbarRow
  const height = Math.max(row, xs.length * row) + 4
  const labelW = Math.min(160, Math.round(width * 0.32))
  const single = plotted.length === 1
  const valueW = single ? 72 : 12
  const left = labelW + 8
  const right = width - valueW
  const lo = Math.min(0, model.min)
  const hi = Math.max(0, model.max) || 1
  const x = linear(lo, hi, left, right)
  const zero = x(0)
  const k = Math.max(1, plotted.length)
  const thick = Math.min(MARK.barMax, row - 8)
  const sub = Math.max(2, (thick - MARK.gap * (k - 1)) / k)
  const labels = xs.map((v) => xLabel(chart, v))

  const rowsAt = (i: number): TooltipRow[] =>
    plotted.map((p) => ({
      key: p.key,
      label: p.label,
      color: single ? undefined : p.color,
      value: p.values[i] ?? null,
      n: p.ns[i] ?? null,
    }))

  const drill = (i: number, s: number) => {
    const p = plotted[s]
    const xv = xs[i]
    if (!onPointClick || !p || xv === undefined || (p.values[i] ?? null) === null) return
    onPointClick({ x: xv, series: p.key })
  }

  const geom = xs.flatMap((xv, i) => {
    const y0 = i * row + (row - thick) / 2
    return plotted.map((p, s) => ({ xv, i, s, p, v: p.values[i] ?? null, by: y0 + s * (sub + MARK.gap) }))
  })

  return (
    <div ref={ref} className="reports-root relative w-full" data-chart-kind="hbar">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={summarise('Ranked bar chart', chart.title, plotted, labels[0] ?? '', labels[labels.length - 1] ?? '', xs.length)}
        className="block overflow-visible"
      >
        <line x1={zero + 0.5} x2={zero + 0.5} y1={0} y2={height - 4} stroke="var(--color-border-strong)" strokeWidth={1} />
        {xs.map((xv, i) => {
          const label = labels[i] ?? xv
          const v = single ? (plotted[0]?.values[i] ?? null) : null
          return (
            <g key={xv}>
              <text x={labelW} y={i * row + row / 2} dy="0.32em" textAnchor="end" fontSize={12} fill={INK.primary}>
                <title>{label}</title>
                {label.length > 22 ? `${label.slice(0, 21)}…` : label}
              </text>
              {single ? (
                <text
                  x={v === null ? zero + 6 : Math.max(x(v), zero) + 6}
                  y={i * row + row / 2}
                  dy="0.32em"
                  fontSize={12}
                  fill={v === null ? INK.secondary : INK.primary}
                  className="tabular"
                >
                  {v === null ? 'no data' : formatValue(chart.unit, v)}
                </text>
              ) : null}
            </g>
          )
        })}
        {geom.map(({ xv, i, s, p, v, by }) =>
          v === null || v === 0 ? null : (
            <path
              key={`${xv}:${p.key}`}
              data-mark-paint
              data-x={xv}
              data-series={p.key}
              d={hBarPath(by, sub, zero, x(v), MARK.radius)}
              fill={p.color}
              style={hover?.i === i && hover.s === s ? { filter: 'brightness(1.12)' } : undefined}
            />
          ),
        )}
      </svg>
      <div className="absolute top-0 left-0 w-full" style={{ height }}>
        {geom.map(({ xv, i, s, p, v, by }) => (
          <MarkButton
            key={`${xv}:${p.key}`}
            x={0}
            y={single ? i * row : by - MARK.gap / 2}
            w={width}
            h={single ? row : sub + MARK.gap}
            label={tooltipText(
              labels[i] ?? xv,
              rowsAt(i).filter((r) => r.key === p.key),
              chart.unit,
            )}
            clickable={v !== null && !!onPointClick}
            empty={v === null}
            dataX={xv}
            dataSeries={p.key}
            onHover={() => setHover({ i, s })}
            onLeave={() => setHover(null)}
            onActivate={() => drill(i, s)}
          />
        ))}
      </div>
      {hover ? (
        <ChartTooltip
          x={Math.min(width - 8, right)}
          y={hover.i * row + row}
          containerWidth={width}
          title={labels[hover.i] ?? ''}
          rows={rowsAt(hover.i)}
          unit={chart.unit}
        />
      ) : null}
    </div>
  )
}
